import { Badge } from '@playbooks/ui/components/badge';
import { Button } from '@playbooks/ui/components/button';
import type { CardProps as PrimitiveCardProps } from '@playbooks/ui/components/card';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@playbooks/ui/components/card';
import { CardMedia } from '@playbooks/ui/components/card';
import { Icon, resolveIcon } from '@playbooks/ui/components/icon';
import { cn } from '@playbooks/ui/lib/utils';
import { capitalize, getSingular } from '@playbooks/utils';

import type { CardButtonInteractionProps, CardContentProps } from './types';

type SearchCardProps = Pick<
	CardContentProps,
	'actions' | 'badges' | 'className' | 'icon' | 'photo' | 'subtitle' | 'text' | 'title'
> & {
	index?: string;
	render?: PrimitiveCardProps['render'];
};
type SearchBtnCardProps = SearchCardProps & CardButtonInteractionProps & { active?: boolean; searchIndex?: string };

export const SearchCard = ({
	icon,
	index,
	photo,
	badges,
	title,
	subtitle,
	text,
	actions,
	render,
	className = '',
}: SearchCardProps) => {
	// Theme
	const rootClassName = cn(
		'flex w-full flex-row items-start justify-between gap-3 rounded-md border-0 bg-transparent px-3 py-2 text-left shadow-none transition-colors group-focus:bg-surface-card-hover group-hover:bg-surface-card-hover',
		className,
	);

	// Render
	return (
		<Card size='sm' render={render} className={rootClassName}>
			<span className='flex min-w-0 flex-1 items-center gap-3'>
				<CardHeader className='flex size-9 shrink-0 items-center justify-center overflow-hidden rounded-md border border-border bg-icon-well p-0 text-foreground-subtle [&_svg]:size-4'>
					{photo ? (
						<img src={photo} alt='' className='size-full object-cover' />
					) : (
						<CardMedia>
							<Icon {...resolveIcon(icon, 'fad')} aria-hidden />
						</CardMedia>
					)}
				</CardHeader>
				<CardContent className='min-w-0 flex-1 p-0'>
					<CardTitle className='flex min-w-0 items-center gap-2'>
						<span className='truncate'>{title}</span>
						{badges && <span className='flex shrink-0 items-center gap-1.5'>{badges}</span>}
					</CardTitle>
					{subtitle && <CardDescription className='truncate'>{subtitle}</CardDescription>}
					{text && <CardDescription className='truncate'>{text}</CardDescription>}
				</CardContent>
			</span>
			{actions ||
				(index ? (
					<Badge variant='outline' className='shrink-0 text-foreground-body'>
						{capitalize(getSingular(index))}
					</Badge>
				) : null)}
		</Card>
	);
};

export const SearchBtnCard = ({ active, searchIndex, ...props }: SearchBtnCardProps) => (
	<SearchCard
		{...props}
		render={
			<Button
				variant='ghost'
				aria-label={String(props.title)}
				disabled={props.disabled}
				isLoading={props.isLoading}
				onClick={props.onClick}
				onFocus={props.onFocus}
				onMouseEnter={props.onMouseEnter}
				data-active={active || undefined}
				data-search-index={searchIndex}
				className='group h-auto w-full cursor-pointer justify-start rounded-md p-0 text-left outline-none data-[active=true]:bg-surface-selected'
			/>
		}
	/>
);

export type { SearchBtnCardProps, SearchCardProps };
